import { safeReturnToPath } from '@/lib/safeRedirect';

// SAB-575: centralized copy + intent helpers for the landing auth entry.
//
// The compliance-sensitive strings below are PLACEHOLDERS pending legal review
// (Dan). Each is marked `COPY: pending legal (SAB-575)`. Swap the final wording
// HERE in one place — every surface (nav, hero, signup, desktop card) imports
// from this module, so there is no hunt across components.
//
// Do NOT invent new legal claims. If legal changes a phrase, edit only the
// string; the structure around it is copy-agnostic.

// COPY: pending legal (SAB-575)
// Hero eligibility line. Nebraska spelled out on BOTH breakpoints (fixes the old
// mobile "19+ NE" inconsistency).
export const HERO_ELIGIBILITY_LINE =
  "Free to start · you'll be the commissioner · 18+ (19+ in Nebraska), eligible states only";

// COPY: pending legal (SAB-575)
// Signup screen verification note. Open items flagged by design: "play free right
// away" may over-promise if free leagues carry gating; "add money" may need to be
// legal's preferred term over "deposit".
export const SIGNUP_VERIFICATION_NOTE =
  "Create leagues and play free right away. We verify identity and location only when you add money — 18+ (19+ in Nebraska), eligible states only.";

// COPY: pending legal (SAB-575)
// Desktop inline signup-card body.
export const SIGNUP_CARD_BODY =
  "Free to join. Add money only when you enter a paid league. Starting a league? Same first step.";

// COPY: pending legal (SAB-575)
// Short eligibility line under the desktop signup card.
export const CARD_ELIGIBILITY_LINE =
  "Free to join · 18+ (19+ in Nebraska) · eligible states only";

// ── Commissioner intent ────────────────────────────────────────────────────
// Carried in the ROUTE (?intent=create-league), never component state, so it
// survives the OAuth redirect round-trip and the login<->signup hop.
export const CREATE_LEAGUE_INTENT = 'create-league';

// Where "Create a League" lands after auth. NFL league setup (ProtectedRoute).
export const LEAGUE_SETUP_ROUTE = '/nfl/create';

// SAB-587: authed-home default for plain (no-intent) signup/login. Points at the
// /home gate, which resolves the landing fallback chain (active NFL -> in_season
// MLB -> any NFL -> NFL create/join) in one place. Was '/beta', a hard switch to
// MLB that landed NFL users on the wrong sport.
export const DEFAULT_AUTHED_HOME = '/home';

/**
 * Resolve where auth should land, given the ?intent param, an optional `from`,
 * and an optional `returnTo` (?returnTo=).
 *
 * SAB-592: a VALID returnTo wins over everything. This is how an invite link
 * survives sign-up/sign-in: the invitee is sent to /signup?returnTo=/nfl/join/CODE,
 * and after auth they go to the invite rather than the default home (which, via the
 * SAB-587 landing gate, would route them into their OWN league and silently drop
 * the invite). returnTo is validated (same-origin relative path only) to avoid an
 * open redirect through the login flow.
 */
export function resolveAuthTarget(
  intent: string | null,
  from?: string | null,
  returnTo?: string | null,
): string {
  const safeReturn = safeReturnToPath(returnTo);
  if (safeReturn) return safeReturn;
  if (intent === CREATE_LEAGUE_INTENT) return LEAGUE_SETUP_ROUTE;
  return from || DEFAULT_AUTHED_HOME;
}

export const hasCreateLeagueIntent = (intent: string | null) =>
  intent === CREATE_LEAGUE_INTENT;

/** Build a /login or /signup href that PRESERVES intent + returnTo across the
 *  login<->signup hop (SAB-592: an invitee bouncing to "Log In" must keep the
 *  invite). */
export function authHref(
  base: '/login' | '/signup',
  intent: string | null,
  returnTo?: string | null,
): string {
  const q = new URLSearchParams();
  if (intent) q.set('intent', intent);
  if (returnTo) q.set('returnTo', returnTo);
  const qs = q.toString();
  return qs ? `${base}?${qs}` : base;
}
